/**
 * Push Campaign Manager - Admin component for managing push notification campaigns
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  createCampaign, 
  listCampaigns, 
  sendCampaignNow, 
  cancelCampaign, 
  deleteCampaign 
} from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Bell, 
  Plus, 
  Send, 
  Calendar, 
  Users, 
  Loader2,
  Play,
  Pause,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const TARGET_TYPES = [
  { value: 'inactive_users', label: 'Inactive Users', description: 'Users who haven\'t logged in recently' },
  { value: 'all_users', label: 'All Users', description: 'Send to everyone with push enabled' },
  { value: 'new_users', label: 'New Users', description: 'Users who signed up in the last 7 days' },
  { value: 'kyc_pending', label: 'KYC Pending', description: 'Users who haven\'t completed KYC' },
];

const STATUS_BADGES = {
  draft: { color: 'bg-gray-500/10 text-gray-600', icon: Clock },
  scheduled: { color: 'bg-blue-500/10 text-blue-600', icon: Calendar },
  sending: { color: 'bg-yellow-500/10 text-yellow-600', icon: Loader2 },
  completed: { color: 'bg-emerald-500/10 text-emerald-600', icon: CheckCircle },
  cancelled: { color: 'bg-red-500/10 text-red-600', icon: XCircle },
};

const PushCampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    title: '',
    body: '',
    target_type: 'inactive_users',
    inactive_days: 7,
    scheduled_at: ''
  });

  const fetchCampaigns = async () => {
    try {
      const response = await listCampaigns({ limit: 50 });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleCreate = async () => {
    if (!newCampaign.title || !newCampaign.body) {
      toast.error('Title and message body are required');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        title: newCampaign.title,
        body: newCampaign.body,
        target_type: newCampaign.target_type,
        inactive_days: parseInt(newCampaign.inactive_days),
        scheduled_at: newCampaign.scheduled_at || null
      };
      
      const response = await createCampaign(payload);
      toast.success(`Campaign created! Target: ${response.data.target_count} users`);
      
      setShowCreateDialog(false);
      setNewCampaign({
        title: '',
        body: '',
        target_type: 'inactive_users',
        inactive_days: 7,
        scheduled_at: ''
      });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleSendNow = async (campaignId) => {
    try {
      const response = await sendCampaignNow(campaignId);
      toast.success(`Campaign sent to ${response.data.sent_count} users!`);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };

  const handleCancel = async (campaignId) => {
    try {
      await cancelCampaign(campaignId);
      toast.success('Campaign cancelled');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel campaign');
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await deleteCampaign(campaignId);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <Card data-testid="push-campaign-manager">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Bell className="w-5 h-5 text-emerald-500" />
                Push Campaigns
              </CardTitle>
              <CardDescription>
                Create and manage promotional push notifications for users
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-xl"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No campaigns yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((campaign) => {
                const StatusIcon = STATUS_BADGES[campaign.status]?.icon || Clock;
                const statusColor = STATUS_BADGES[campaign.status]?.color || 'bg-gray-500/10 text-gray-600';
                
                return (
                  <div
                    key={campaign.id}
                    className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium truncate">{campaign.title}</h3>
                          <Badge className={statusColor}>
                            <StatusIcon className={`w-3 h-3 mr-1 ${campaign.status === 'sending' ? 'animate-spin' : ''}`} />
                            {campaign.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {campaign.body}
                        </p>
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            Target: {campaign.stats?.target_count || 0} users
                          </span>
                          {campaign.scheduled_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Scheduled: {formatDate(campaign.scheduled_at)}
                            </span>
                          )}
                          {campaign.status === 'completed' && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                              Sent: {campaign.stats?.sent_count || 0}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendNow(campaign.id)}
                              className="text-emerald-600"
                              data-testid={`send-campaign-${campaign.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Send Now
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancel(campaign.id)}
                              className="text-orange-500"
                            >
                              <Pause className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {(campaign.status === 'draft' || campaign.status === 'cancelled') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(campaign.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Push Campaign</DialogTitle>
            <DialogDescription>
              Send promotional notifications to users
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Notification Title</Label>
              <Input
                id="title"
                placeholder="e.g., Don't miss out!"
                value={newCampaign.title}
                onChange={(e) => setNewCampaign({ ...newCampaign, title: e.target.value })}
                data-testid="campaign-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="body">Message Body</Label>
              <Textarea
                id="body"
                placeholder="e.g., Complete your profile and get a bonus!"
                value={newCampaign.body}
                onChange={(e) => setNewCampaign({ ...newCampaign, body: e.target.value })}
                rows={3}
                data-testid="campaign-body-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Select 
                value={newCampaign.target_type} 
                onValueChange={(v) => setNewCampaign({ ...newCampaign, target_type: v })}
              >
                <SelectTrigger data-testid="campaign-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newCampaign.target_type === 'inactive_users' && (
              <div className="space-y-2">
                <Label htmlFor="inactive_days">Inactive for (days)</Label>
                <Input
                  id="inactive_days"
                  type="number"
                  min="1"
                  max="365"
                  value={newCampaign.inactive_days}
                  onChange={(e) => setNewCampaign({ ...newCampaign, inactive_days: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="scheduled_at">Schedule (optional)</Label>
              <Input
                id="scheduled_at"
                type="datetime-local"
                value={newCampaign.scheduled_at}
                onChange={(e) => setNewCampaign({ ...newCampaign, scheduled_at: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Leave empty to send manually</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newCampaign.title || !newCampaign.body}
              data-testid="create-campaign-confirm-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Campaign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PushCampaignManager;
